// src/pages/Onboarding.js
//
// THE ONBOARDING PAGE (Record 675; Bob, 2026-10-13). One page, every role: what each person does to come onto the
// platform, how, and where - and, for a General Chairperson, the eight boxes his committee's documents go into.
// Every word is src\data\onboardingGuide.js (and, for members, src\data\applicantGuide.js); this file only lays
// them out. The upload boxes write to onboarding/<committee>/<slot>/ in Storage; storage.rules decides who may.
//
// Files cannot be deleted from this screen (storage.rules allows delete to the administrator only): a mistaken
// upload is reported by e-mail, and nothing a General Chairperson sends can be lost by a stray click.

import React, { useCallback, useEffect, useState } from 'react';
import { ref, listAll, getMetadata, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import {
  SITE_URL, SUPPORT_EMAIL, MAX_LABEL, ALLOWED_ACCEPT, ALLOWED_LABEL, SLOTS,
  GC_TITLE, GC_STEPS, LC_TITLE, LC_STEPS, MEMBER_TITLE, MEMBER_HOW, EVERYONE_TITLE, EVERYONE,
  fileStatus, validateFile, objectPath, onboardingCommitteeOf, sectionOrder,
} from '../data/onboardingGuide';
import { PROCESS_TITLE, PROCESS, ROLES_TITLE, ROLES } from '../data/applicantGuide';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const box = { background: C.panel, border: '1px solid ' + C.line, borderRadius: 6, padding: '14px 16px', marginBottom: 14 };
const h3 = { color: C.accent, fontSize: 15, fontWeight: 700, margin: '0 0 10px' };

function Steps({ steps }) {
  return (
    <ol style={{ margin: 0, paddingLeft: 20, color: C.text, fontSize: 13, lineHeight: 1.6 }}>
      {steps.map(([head, body], i) => (
        <li key={i} style={{ marginBottom: 6 }}><b>{head}</b> {body}</li>
      ))}
    </ol>
  );
}

function Slot({ slot, committee, user }) {
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const folder = 'onboarding/' + committee + '/' + slot.id;

  const load = useCallback(async () => {
    try {
      const res = await listAll(ref(storage, folder));
      const rows = await Promise.all(res.items.map(async (it) => {
        let meta = null;
        try { meta = await getMetadata(it); } catch (e) { /* not yet scanned: the rules refuse a read until it is */ }
        const nm = (meta && meta.customMetadata && meta.customMetadata.originalName) || it.name.replace(/^[0-9TZ-]+_/, '');
        return { path: it.fullPath, name: nm, meta, when: meta ? meta.timeCreated : '' };
      }));
      rows.sort((a, b) => String(b.when).localeCompare(String(a.when)));
      setFiles(rows);
    } catch (e) {
      setErr('This box could not be read (' + ((e && e.code) || 'unavailable') + ').');
      setFiles([]);
    }
  }, [folder]);

  useEffect(() => { load(); }, [load]);

  const add = async (list) => {
    setErr('');
    const chosen = Array.from(list || []);
    for (const f of chosen) {
      const bad = validateFile(f);
      if (bad) { setErr(bad); return; }
    }
    try {
      for (const f of chosen) {
        setBusy('Uploading ' + f.name + '...');
        await uploadBytes(ref(storage, objectPath(committee, slot.id, f.name)), f, {
          contentType: f.type,
          customMetadata: { committee, slot: slot.id, uploadedBy: user.uid, originalName: String(f.name).slice(0, 200), note: note.slice(0, 500) },
        });
      }
      setNote('');
    } catch (e) {
      setErr('The upload was refused (' + ((e && e.code) || 'unavailable') + '). E-mail ' + SUPPORT_EMAIL + '.');
    } finally {
      setBusy('');
      load();
    }
  };

  const count = files ? files.length : 0;
  return (
    <div style={{ ...box, borderColor: count ? C.ok : C.line }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <div style={{ color: C.text, fontWeight: 700 }}>{slot.n}. {slot.title}</div>
        <div style={{ fontSize: 12, color: count ? C.ok : C.dim }}>{files === null ? 'Reading...' : count ? count + ' file' + (count === 1 ? '' : 's') : 'Nothing yet'}</div>
      </div>
      <div style={{ color: C.text, fontSize: 13, marginTop: 6 }}>{slot.what}</div>
      <div style={{ color: C.dim, fontSize: 12, marginTop: 4 }}>{slot.note}</div>
      {slot.where ? <div style={{ color: C.dim, fontSize: 12, marginTop: 4 }}>Where to find it: {slot.where}</div> : null}
      {files && files.length > 0 && (
        <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12, color: C.text }}>
          {files.map((r) => (
            <li key={r.path}>{r.name} <span style={{ color: fileStatus(r.meta) === 'Received' ? C.ok : C.dim }}>- {fileStatus(r.meta)}</span></li>
          ))}
        </ul>
      )}
      <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input aria-label="Note for LOTR (optional)" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for LOTR (optional)"
          style={{ flex: '1 1 220px', fontSize: 12, padding: '6px 8px', background: '#132235', color: C.text, border: '1px solid ' + C.line, borderRadius: 4 }} />
        <label className="btn btn-primary btn-sm" style={{ cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Uploading...' : 'Add files'}
          <input aria-label="Attach your onboarding documents" type="file" multiple accept={ALLOWED_ACCEPT} disabled={!!busy} style={{ display: 'none' }}
            onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        </label>
      </div>
      {busy && <div style={{ fontSize: 12, color: C.accent, marginTop: 6 }}>{busy}</div>}
      {err && <div style={{ fontSize: 12, color: C.bad, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function Uploads({ user }) {
  const own = onboardingCommitteeOf(user);
  const [picked, setPicked] = useState('');
  const [known, setKnown] = useState([]);
  useEffect(() => {
    if (!user || !user.isAdmin) return;
    listAll(ref(storage, 'onboarding')).then((r) => setKnown(r.prefixes.map((p) => p.name))).catch(() => setKnown([]));
  }, [user]);
  const committee = own || (user && user.isAdmin ? picked : '');
  if (!own && !(user && user.isAdmin)) return null;
  return (
    <div style={{ ...box, borderColor: C.accent }}>
      <h3 style={h3}>Your committee's documents{committee ? ' - ' + committee : ''}</h3>
      <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6, marginBottom: 10 }}>
        Eight boxes, one per item. Items 1 to 7 are required by the Opening Date; item 8 is optional.
        Each box takes any number of files, up to {MAX_LABEL} each: {ALLOWED_LABEL}. A phone photo of each page is fine; nothing needs retyping.
        Only you and LOTR's administrator can open what you upload here.
      </div>
      {user && user.isAdmin && !own && (
        <div style={{ marginBottom: 12, fontSize: 12, color: C.dim }}>
          Administrator - committee:{' '}
          <input aria-label="GO-256" value={picked} onChange={(e) => setPicked(e.target.value.trim().toUpperCase())} placeholder="GO-256"
            style={{ fontSize: 12, padding: '4px 6px', width: 110, background: '#132235', color: C.text, border: '1px solid ' + C.line, borderRadius: 4 }} />
          {known.length ? ' On file: ' + known.join(', ') : ' None on file yet.'}
        </div>
      )}
      {committee && /^GO-[A-Z0-9]+$/.test(committee)
        ? SLOTS.map((s) => <Slot key={committee + s.id} slot={s} committee={committee} user={user} />)
        : user && user.isAdmin ? <div style={{ fontSize: 12, color: C.dim }}>Type a committee (GO-nnn) to open its boxes.</div> : null}
    </div>
  );
}

export default function Onboarding({ user }) {
  const sections = {
    gc: (
      <div key="gc" style={box}>
        <h3 style={h3}>{GC_TITLE}</h3>
        <Steps steps={GC_STEPS} />
      </div>
    ),
    lc: (
      <div key="lc" style={box}>
        <h3 style={h3}>{LC_TITLE}</h3>
        <Steps steps={LC_STEPS} />
      </div>
    ),
    member: (
      <div key="member" style={box}>
        <h3 style={h3}>{MEMBER_TITLE}</h3>
        <Steps steps={MEMBER_HOW} />
        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, margin: '12px 0 6px' }}>{PROCESS_TITLE}</div>
        <Steps steps={PROCESS} />
        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, margin: '12px 0 6px' }}>{ROLES_TITLE}</div>
        <ul style={{ margin: 0, paddingLeft: 20, color: C.text, fontSize: 13, lineHeight: 1.6 }}>
          {ROLES.map(([head, body], i) => <li key={i}><b>{head}</b> {body}</li>)}
        </ul>
      </div>
    ),
    everyone: (
      <div key="everyone" style={box}>
        <h3 style={h3}>{EVERYONE_TITLE}</h3>
        <ul style={{ margin: 0, paddingLeft: 20, color: C.text, fontSize: 13, lineHeight: 1.6 }}>
          {EVERYONE.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </div>
    ),
  };
  return (
    <div className="page">
      <div className="page-header">
        <h1 style={{ margin: 0, fontSize: 20 }}>Onboarding</h1>
        <p>Who does what to come onto the platform, how, and where. Your own part is first. Site: {SITE_URL}</p>
      </div>
      {sectionOrder(user)[0] === 'gc' && <Uploads user={user} />}
      {sectionOrder(user).map((k) => sections[k])}
    </div>
  );
}
